import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { useBrand } from '../contexts/BrandContext';
import {
  createOpportunity,
  updateOpportunity,
  getPipelineStages,
  type Opportunity,
  type PipelineStage,
} from '../lib/crmService';

interface OpportunityFormProps {
  opportunity?: Opportunity | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function OpportunityForm({ opportunity, onClose, onSuccess }: OpportunityFormProps) {
  const { selectedBrand } = useBrand();
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    client_name: opportunity?.client_name || '',
    company_name: opportunity?.company_name || '',
    email: opportunity?.email || '',
    phone: opportunity?.phone || '',
    value: opportunity?.value || 0,
    probability: opportunity?.probability || 50,
    expected_close_date: opportunity?.expected_close_date || '',
    source: opportunity?.source || 'manual',
    notes: opportunity?.notes || '',
    pipeline_stage_id: opportunity?.pipeline_stage_id || '',
  });

  useEffect(() => {
    if (selectedBrand) {
      loadStages();
    }
  }, [selectedBrand]);

  const loadStages = async () => {
    if (!selectedBrand) return;
    const stagesData = await getPipelineStages(selectedBrand.id);
    setStages(stagesData);
    if (!formData.pipeline_stage_id && stagesData.length > 0) {
      setFormData((prev) => ({ ...prev, pipeline_stage_id: stagesData[0].id }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBrand) return;

    try {
      setLoading(true);

      const sanitizedData = {
        ...formData,
        value: Number(formData.value),
        expected_close_date: formData.expected_close_date || null,
      };

      if (opportunity) {
        const result = await updateOpportunity(opportunity.id, sanitizedData);
        if (result.success) {
          onSuccess();
          onClose();
        } else {
          console.error('Update opportunity failed:', result.error);
          alert(result.error || 'Failed to update opportunity');
        }
      } else {
        const result = await createOpportunity({
          ...sanitizedData,
          brand_id: selectedBrand.id,
        });
        if (result.success) {
          onSuccess();
          onClose();
        } else {
          alert(result.error || 'Failed to create opportunity');
        }
      }
    } catch (error) {
      console.error('Error saving opportunity:', error);
      alert('An error occurred while saving the opportunity');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50 p-0 md:p-4">
      <div className="bg-white rounded-t-2xl md:rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] md:max-h-[85vh] overflow-hidden flex flex-col animate-slide-up md:animate-none">
        <div className="p-4 md:p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 md:hidden"></div>
          <h3 className="text-lg md:text-xl font-bold text-gray-900">
            {opportunity ? 'Edit Opportunity' : 'Create New Opportunity'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 active:text-gray-800 p-2 -mr-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Name *
              </label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) => handleChange('client_name', e.target.value)}
                required
                inputMode="text"
                autoComplete="name"
                className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name
              </label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                placeholder="Acme Corp"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Opportunity Value *
              </label>
              <input
                type="number"
                value={formData.value}
                onChange={(e) => handleChange('value', parseFloat(e.target.value) || 0)}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                placeholder="5000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Probability (%)
              </label>
              <input
                type="number"
                value={formData.probability}
                onChange={(e) => handleChange('probability', parseInt(e.target.value) || 0)}
                min="0"
                max="100"
                className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                placeholder="50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pipeline Stage *
              </label>
              <select
                value={formData.pipeline_stage_id}
                onChange={(e) => handleChange('pipeline_stage_id', e.target.value)}
                required
                className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              >
                <option value="">Select a stage</option>
                {stages.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Close Date
              </label>
              <input
                type="date"
                value={formData.expected_close_date}
                onChange={(e) => handleChange('expected_close_date', e.target.value)}
                className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source
              </label>
              <select
                value={formData.source}
                onChange={(e) => handleChange('source', e.target.value)}
                className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              >
                <option value="manual">Manual Entry</option>
                <option value="referral">Referral</option>
                <option value="website">Website</option>
                <option value="cold_call">Cold Call</option>
                <option value="social_media">Social Media</option>
                <option value="advertisement">Advertisement</option>
                <option value="partner">Partner</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              placeholder="Add any additional notes or context..."
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-4 border-t border-gray-200 sticky bottom-0 bg-white -mx-4 md:-mx-6 px-4 md:px-6 pb-4 md:pb-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 md:py-2 text-gray-700 hover:text-gray-900 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors text-center"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-sm transition-all"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : opportunity ? 'Update' : 'Create'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
